/**
 * LeftNav — v3 vertical navigation column
 *
 * Three states: expanded (180px) / collapsed (48px) / hidden.
 * State is owned by PanelContext (persisted in localStorage).
 * Shares the canvas gradient — transparent bg.
 * Toggle cycles: expanded → collapsed → hidden → expanded.
 * Mobile (<768px): always hidden. Toggle opens full-screen overlay (future).
 *
 * Spec: docs/superpowers/specs/2026-03-17-layout-information-density-design.md §1.2
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, Settings } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { ViewModeSelector, type ViewMode } from './ViewModeSelector';
import { FlameIcon } from './FlameIcon';
import { FacetPills, type FacetItem } from './FacetPills';
import { usePanels } from '../../contexts/PanelContext';

/** Demo data — in production these come from API query counts */
const STATUS_FACETS: FacetItem[] = [
  { key: 'action-required', label: 'Action Req', count: 12, dotColor: '#f59e0b' },
  { key: 'approved', label: 'Approved', count: 34, dotColor: '#22c55e' },
  { key: 'expired', label: 'Expired', count: 8, dotColor: '#94a3b8' },
];

const SEVERITY_FACETS: FacetItem[] = [
  { key: 'extreme', label: 'Extreme', count: 3, dotColor: '#ef4444' },
  { key: 'high', label: 'High', count: 15, dotColor: '#f59e0b' },
  { key: 'moderate', label: 'Moderate', count: 28, dotColor: '#3b82f6' },
];

export type LeftNavState = 'expanded' | 'collapsed' | 'hidden';

export function LeftNav() {
  const { leftNavState } = usePanels();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('excel');
  const isAdmin = user?.role === 'admin';

  if (leftNavState === 'hidden') {
    return null;
  }

  const isCollapsed = leftNavState === 'collapsed';
  const width = isCollapsed ? 'w-12' : 'w-[180px]';

  return (
    <aside
      data-testid="left-nav"
      data-state={leftNavState}
      aria-label="Primary navigation"
      className={[
        'hidden md:flex flex-col sticky top-0 transition-[width] duration-200 shrink-0',
        // Height stops above BottomSheetTabs: 40px mobile / 34px desktop
        'h-[calc(100vh-40px)] xl:h-[calc(100vh-34px)]',
        width,
        // Right divider — spec §1.2: 1px solid rgba(0,0,0,0.1) / rgba(255,255,255,0.08)
        'border-r border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.08)]',
      ].join(' ')}
    >
      {/* Header (44px) — flame icon + product name */}
      <div
        className={[
          'flex items-center h-11 shrink-0',
          isCollapsed ? 'justify-center' : 'px-3 gap-2',
          // Header bottom border (same as right divider)
          'border-b border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.08)]',
        ].join(' ')}
      >
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="left-nav-brand">
          <FlameIcon size={20} id="leftnav-flame" />
          {/* Product name — 9px, 600 weight, secondary color, 1.5px letter-spacing, uppercase (§4.3 special) */}
          {!isCollapsed && (
            <span
              className="text-[9px] font-semibold uppercase tracking-[1.5px] text-etched whitespace-nowrap
                text-t2"
            >
              Firebreak Bacchus
            </span>
          )}
        </Link>
      </div>

      {/* Body — scrollable content */}
      <div className="flex-1 overflow-y-auto py-3">
        {isCollapsed ? (
          /* Collapsed: icon column — spec §1.2 collapsed state */
          <div className="flex flex-col items-center gap-1.5">
            <ViewModeSelector value={viewMode} onChange={setViewMode} collapsed />
            {/* Status filter icon */}
            <button
              type="button"
              data-testid="left-nav-filter-icon"
              title="Filters"
              className="flex items-center justify-center w-7 h-7 rounded-md transition-colors
                text-t2
                hover:bg-[rgba(255,255,255,0.3)] dark:hover:bg-[rgba(255,255,255,0.06)]"
            >
              <SlidersHorizontal size={16} strokeWidth={1.5} />
            </button>
            {/* Search icon */}
            <button
              type="button"
              data-testid="left-nav-search-icon"
              title="Search"
              className="flex items-center justify-center w-7 h-7 rounded-md transition-colors
                text-t2
                hover:bg-[rgba(255,255,255,0.3)] dark:hover:bg-[rgba(255,255,255,0.06)]"
            >
              <Search size={16} strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          /* Expanded: full nav — spec §1.2 expanded state
             flex-col + min-h-full so mt-auto pushes filter input to bottom */
          <div className="px-3 flex flex-col min-h-full gap-4">
            {/* View mode selector — shared state with collapsed via lifted viewMode */}
            <ViewModeSelector value={viewMode} onChange={setViewMode} />

            {/* Status facets — spec §1.2 section 2 */}
            <FacetPills sectionLabel="Status" items={STATUS_FACETS} />

            {/* Severity facets — spec §1.2 section 3 */}
            <FacetPills sectionLabel="Severity" items={SEVERITY_FACETS} />

            {/* Admin link — visible only for admin users */}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="left-nav-admin"
                className="flex items-center gap-2 px-2 py-1.5 rounded-v3-pill text-whisper-sm text-etched text-t2 hover:bg-[rgba(255,255,255,0.15)] dark:hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              >
                <Settings size={14} strokeWidth={1.5} />
                Admin Console
              </Link>
            )}

            {/* Filter input — pushed to bottom via mt-auto (works because parent is flex-col) */}
            <div className="mt-auto pt-4">
              <input
                type="text"
                data-testid="left-nav-filter-input"
                placeholder="Filter..."
                className="w-full px-2 py-1.5 text-[10px] rounded-[6px] transition-colors
                  bg-[rgba(255,255,255,0.2)] dark:bg-[rgba(255,255,255,0.04)]
                  text-t1
                  placeholder:text-[#6b6a65] dark:placeholder:text-[#9a9890]
                  focus:outline-none focus:ring-1 focus:ring-[rgba(0,0,0,0.1)] dark:focus:ring-[rgba(255,255,255,0.1)]"
              />
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
